import { useRef, useCallback, useEffect } from 'react';

export function useDesktopZoom(
  imageWrapperRef: React.RefObject<HTMLDivElement | null>,
  isZoomEnabled: boolean,
  setIsZoomEnabled: React.Dispatch<React.SetStateAction<boolean>>,
) {
  const desktopZoomRef = useRef(1);

  const applyDesktopZoom = useCallback((img: HTMLElement) => {
    const s = desktopZoomRef.current;
    img.style.transform = s > 1 ? `scale(${s})` : '';
    img.style.transition = 'transform 0.15s linear';
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (window.innerWidth < 768 || !isZoomEnabled) return;
    const img = imageWrapperRef.current?.querySelector('img:not([aria-hidden="true"])') as HTMLImageElement | null;
    if (!img || !imageWrapperRef.current) return;

    const { left, top, width, height } = imageWrapperRef.current.getBoundingClientRect();
    const x = ((e.clientX - left) / width) * 100;
    const y = ((e.clientY - top) / height) * 100;

    img.style.transformOrigin = `${x}% ${y}%`;
  }, [isZoomEnabled, imageWrapperRef]);

  // Wheel zoom: native listener with { passive: false } to allow preventDefault
  useEffect(() => {
    const el = imageWrapperRef.current;
    if (!el || !isZoomEnabled) return;

    const onWheel = (e: WheelEvent) => {
      if (window.innerWidth < 768) return;
      e.preventDefault();
      const img = el.querySelector('img:not([aria-hidden="true"])') as HTMLImageElement | null;
      if (!img) return;

      const delta = e.deltaY > 0 ? -0.15 : 0.15;
      desktopZoomRef.current = Math.min(4, Math.max(1, desktopZoomRef.current + delta));
      applyDesktopZoom(img);
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [isZoomEnabled, applyDesktopZoom, imageWrapperRef]);

  const handleMouseLeave = useCallback(() => {
    const img = imageWrapperRef.current?.querySelector('img:not([aria-hidden="true"])') as HTMLImageElement | null;
    if (img && desktopZoomRef.current > 1) {
      desktopZoomRef.current = 1;
      applyDesktopZoom(img);
    }
  }, [applyDesktopZoom, imageWrapperRef]);

  // Apply or reset zoom when toggling zoom mode
  useEffect(() => {
    const img = imageWrapperRef.current?.querySelector('img:not([aria-hidden="true"])') as HTMLImageElement | null;
    if (!img) return;

    if (isZoomEnabled) {
      // If zoom was toggled on via button (not image click), apply initial zoom
      if (desktopZoomRef.current <= 1) {
        desktopZoomRef.current = 1.8;
        img.style.transformOrigin = '50% 50%';
        applyDesktopZoom(img);
      }
    } else {
      desktopZoomRef.current = 1;
      img.style.transform = '';
      img.style.transition = 'transform 0.2s ease';
    }
  }, [isZoomEnabled, imageWrapperRef, applyDesktopZoom]);

  const handleImageClick = useCallback((e: React.MouseEvent<HTMLImageElement>) => {
    e.stopPropagation();
    if (isZoomEnabled) {
      setIsZoomEnabled(false);
    } else {
      setIsZoomEnabled(true);
      const img = e.currentTarget;
      const rect = imageWrapperRef.current?.getBoundingClientRect();
      if (rect) {
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        img.style.transformOrigin = `${x}% ${y}%`;
      }
      desktopZoomRef.current = 1.8;
      applyDesktopZoom(img);
    }
  }, [isZoomEnabled, setIsZoomEnabled, imageWrapperRef, applyDesktopZoom]);

  return {
    handleMouseMove,
    handleMouseLeave,
    handleImageClick,
  };
}
